import React, { FC, useState, useCallback } from 'react';
import { Button, Stack, Text } from '@moda/om';
import classNames from 'classnames';
import { DesignerChipIcon } from './DesignerChipIcon';

import './DesignerChip.scss';

export type DesignerChipProps = {
  id: string;
  name: string | null;
  withHoveringEffect?: boolean;
  treatment?: 'name' | 'generic';
  isFollowed: boolean;
  onFollow?: (id: string, name: string | null) => Promise<unknown>;
  onUnfollow?: (id: string, name: string | null) => Promise<unknown>;
};

export const DesignerChip: FC<DesignerChipProps> = ({
  name,
  id,
  isFollowed,
  onFollow,
  onUnfollow,
  withHoveringEffect = true,
  treatment = 'name'
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setLoading] = useState(false);

  const handleClick = useCallback(() => {
    setLoading(true);

    if (isFollowed && name && onUnfollow) return onUnfollow(id, name).then(() => setLoading(false));

    if (onFollow && name) onFollow(id, name).then(() => setLoading(false));
  }, [id, name, isFollowed, onFollow, onUnfollow]);

  const hasBehaviour = Boolean(onFollow && onUnfollow);

  if (!name || !id) {
    return null;
  }

  return (
    <Button
      className={classNames('DesignerChip', {
        'DesignerChip--followed': isFollowed,
        'DesignerChip--generic': treatment === 'generic'
      })}
      chip
      hover={isHovered}
      key={id}
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      secondary
    >
      {treatment === 'generic' ? (
        <Stack alignItems="center" direction="horizontal" space={2}>
          <span className="DesignerChip__generic-text">
            {!isFollowed && 'Follow for Updates'}
            {withHoveringEffect && hasBehaviour && isFollowed && (
              <>
                {!isHovered && 'Following'}

                {isHovered && 'Unfollow'}
              </>
            )}
          </span>

          {hasBehaviour && (
            <DesignerChipIcon
              isFollowed={isFollowed}
              isHovered={isHovered}
              isLoading={isLoading}
              withHoveringEffect={withHoveringEffect}
            />
          )}
        </Stack>
      ) : (
        <>
          <Text className="DesignerChip__designer-name">{name}</Text>

          <Text className="DesignerChip__designer-follow">
            <Stack alignItems="center" direction="horizontal" space={2}>
              {withHoveringEffect && hasBehaviour && isHovered && (
                <>
                  {!isFollowed && 'Follow'}

                  {isFollowed && 'Unfollow'}
                </>
              )}

              {hasBehaviour && (
                <DesignerChipIcon
                  isFollowed={isFollowed === true}
                  isHovered={isHovered}
                  isLoading={isLoading}
                  withHoveringEffect={withHoveringEffect}
                />
              )}
            </Stack>
          </Text>
        </>
      )}
    </Button>
  );
};
